import { createReducer, on } from '@ngrx/store';
import { AuthenticationResult } from '@azure/msal-browser';
import { v4 as uuid } from 'uuid';

import { UserSession } from '@shared/models';
import {
  DprDetails,
  DprDetailsSync,
  DprOnlineApproval,
} from '@shared/models/dpr';
import { OfflineActions } from './actions';
import { WarningType } from '@shared/enums/dpr';

export const OfflineFeatureName = 'offline';

export interface OfflineState {
  dprDetailsList: DprDetails[];
  dprDetails: DprDetails;
  backupUserDetails: UserSession;
  backupAuthenticationResult: AuthenticationResult;
  backupLastDprs: DprDetails[];
  dprDetailsOfflineChanges: DprDetailsSync[];
  syncOfflineGuid: string;
}

export const initialState: OfflineState = {
  dprDetailsList: null,
  dprDetails: null,
  backupUserDetails: null,
  backupAuthenticationResult: null,
  backupLastDprs: null,
  dprDetailsOfflineChanges: null,
  syncOfflineGuid: null,
};

export const offlineReducer = createReducer(
  initialState,
  on(OfflineActions.setDprDetailsList, (state, { dprDetailsList }) => {
    let clearDprDetails = false;
    if (state.dprDetails) {
      const item = state.dprDetailsList?.find(
        (x: any) => x.dprId === state.dprDetails.dprId
      );
      if (item) {
        clearDprDetails = true;
      }
    }

    return {
      ...state,
      dprDetailsList,
      dprDetails: clearDprDetails ? null : state.dprDetails,
    };
  }),
  on(OfflineActions.setDprDetails, (state, { dprDetails }) => ({
    ...state,
    dprDetails: isExtraDprDetails(state, dprDetails?.id)
      ? dprDetails
      : state.dprDetails,
    dprDetailsList: state.dprDetailsList?.map((x) =>
      x.id === dprDetails?.id ? dprDetails : x
    ),
  })),
  on(OfflineActions.updateExistingDprDetails, (state, { dprDetails }) => ({
    ...state,
    dprDetails:
      state.dprDetails && state.dprDetails.id === dprDetails?.id
        ? dprDetails
        : state.dprDetails,
    dprDetailsList: state.dprDetailsList?.map((x) =>
      x.id === dprDetails?.id ? dprDetails : x
    ),
  })),
  on(
    OfflineActions.updateDprDetails,
    (state, { oldDprDetailId, dprDetails }) => ({
      ...state,
      dprDetails: isExtraDprDetails(state, oldDprDetailId)
        ? dprDetails
        : state.dprDetails,
      dprDetailsList: state.dprDetailsList?.map((x) =>
        x.id === oldDprDetailId ? dprDetails : x
      ),
    })
  ),
  on(OfflineActions.setBackupUserDetails, (state, { backupUserDetails }) => ({
    ...state,
    backupUserDetails,
  })),
  on(OfflineActions.setBackupLastDprs, (state, { backupLastDprs }) => ({
    ...state,
    backupLastDprs,
  })),
  on(
    OfflineActions.setBackupAuthenticationResult,
    (state, { backupAuthenticationResult }) => ({
      ...state,
      backupAuthenticationResult,
    })
  ),
  on(OfflineActions.addDprDetails, (state, { dprDetails }) => {
    const existingItem =
      state.dprDetailsList?.some((x) => x.id === dprDetails.id) ||
      state.dprDetails?.id === dprDetails.id;

    const updatedState = {
      ...state,
      dprDetailsList: state.dprDetailsList?.map((x) =>
        x.id === dprDetails.id ? dprDetails : x
      ),
      dprDetails:
        state.dprDetails?.id === dprDetails.id ? dprDetails : state.dprDetails,
    };

    if (!existingItem) {
      updatedState.dprDetailsList = [
        dprDetails,
        ...(state.dprDetailsList ?? []),
      ];
    }
    return updatedState;
  }),
  on(
    OfflineActions.setDprDetailsOfflineChanges,
    (state, { dprDetailsOfflineChanges }) => ({
      ...state,
      dprDetailsOfflineChanges,
    })
  ),
  on(OfflineActions.updateDprDetailsOfflineChanges, (state, { changes }) => ({
    ...state,
    dprDetailsOfflineChanges: state.dprDetailsOfflineChanges?.some(
      (x) => x.id === changes?.id
    )
      ? state.dprDetailsOfflineChanges?.map((x) =>
          x.id === changes?.id ? changes : x
        )
      : [...(state.dprDetailsOfflineChanges ?? []), changes],
  })),

  //#region Dpr Card

  on(
    OfflineActions.setDprDetailsSignedReport,
    (state, { dprDetailId, attachment }) => ({
      ...state,
      dprDetailsList: state.dprDetailsList.map((x: DprDetails) =>
        x.id === dprDetailId
          ? {
              ...x,
              isSignDprDetailsUploaded: !!attachment,
              signedReportExternalUrl: attachment?.externalUrl,
              signedReportName: attachment?.name,
              signedReportFileType: attachment?.type,
            }
          : x
      ),
    })
  ),
  on(
    OfflineActions.setDprDetailsLockedDate,
    (state, { dprDetailId, lockedDate }) => ({
      ...state,
      dprDetails:
        state.dprDetails?.id === dprDetailId
          ? {
              ...state.dprDetails,
              lockedDate,
              isSignDprDetailsUploaded: false,
            }
          : state.dprDetails,
      dprDetailsList: state.dprDetailsList.map((x: DprDetails) =>
        x.id === dprDetailId
          ? {
              ...x,
              lockedDate,
              isSignDprDetailsUploaded: false,
            }
          : x
      ),
    })
  ),
  on(
    OfflineActions.setDprDetailsNewRevision,
    (state, { dprDetailId, dprDetails }) => ({
      ...state,
      dprDetails: isExtraDprDetails(state, dprDetailId)
        ? dprDetails
        : state.dprDetails,
      dprDetailsList: state.dprDetailsList.map((x: DprDetails) =>
        x.id === dprDetailId ? dprDetails : x
      ),
    })
  ),
  on(OfflineActions.addDprApprover, (state, { dprDetailId, dprApprover }) => ({
    ...state,
    dprDetails:
      state.dprDetails?.id === dprDetailId
        ? {
            ...state.dprDetails,
            dprApprovers: [...state.dprDetails.dprApprovers, dprApprover],
          }
        : state.dprDetails,
    dprDetailsList: state.dprDetailsList.map((x: DprDetails) =>
      x.id === dprDetailId
        ? {
            ...x,
            dprApprovers: [...x.dprApprovers, dprApprover],
          }
        : x
    ),
  })),
  on(
    OfflineActions.updateDprApprover,
    (state, { dprDetailId, dprApprover }) => ({
      ...state,
      dprDetails:
        state.dprDetails?.id === dprDetailId
          ? {
              ...state.dprDetails,
              dprApprovers: state.dprDetails.dprApprovers.map((x) =>
                x.id === dprApprover.id ? dprApprover : x
              ),
            }
          : state.dprDetails,
      dprDetailsList: state.dprDetailsList.map((x: DprDetails) =>
        x.id === dprDetailId
          ? {
              ...x,
              dprApprovers: x.dprApprovers.map((a) =>
                a.id === dprApprover.id ? dprApprover : a
              ),
            }
          : x
      ),
    })
  ),
  on(
    OfflineActions.deleteDprApprover,
    (state, { dprDetailId, dprApproverId }) => ({
      ...state,
      dprDetails:
        state.dprDetails?.id === dprDetailId
          ? {
              ...state.dprDetails,
              dprApprovers: state.dprDetails.dprApprovers.filter(
                (x) => x.id !== dprApproverId
              ),
            }
          : state.dprDetails,
      dprDetailsList: state.dprDetailsList.map((x: DprDetails) =>
        x.id === dprDetailId
          ? {
              ...x,
              dprApprovers: x.dprApprovers.filter(
                (a) => a.id !== dprApproverId
              ),
            }
          : x
      ),
    })
  ),
  on(
    OfflineActions.updateDprOnlineApproval,
    (state, { dprDetailId, dprOnlineApproval }) => ({
      ...state,
      dprDetails:
        state.dprDetails?.id === dprDetailId
          ? {
              ...state.dprDetails,
              dprOnlineApprovals: getUpdatedDprOnlineApprovals(
                state.dprDetails.dprOnlineApprovals,
                dprOnlineApproval
              ),
            }
          : state.dprDetails,
      dprDetailsList: state.dprDetailsList.map((x: DprDetails) =>
        x.id === dprDetailId
          ? {
              ...x,
              dprOnlineApprovals: getUpdatedDprOnlineApprovals(
                x.dprOnlineApprovals,
                dprOnlineApproval
              ),
            }
          : x
      ),
    })
  ),
  on(
    OfflineActions.updateDprApproveStatus,
    (state, { dprDetailId, approveStatus }) => ({
      ...state,
      dprDetails: isExtraDprDetails(state, dprDetailId)
        ? {
            ...state.dprDetails,
            approveStatus,
          }
        : state.dprDetails,
      dprDetailsList: state.dprDetailsList.map((x: DprDetails) =>
        x.id === dprDetailId
          ? {
              ...x,
              approveStatus,
            }
          : x
      ),
    })
  ),

  //#endregion Dpr Card

  //#region Dpr Detail

  on(
    OfflineActions.setDprActivitiesHistory,
    (state, { dprDetailId, dprActivitiesDurationHistory }) => ({
      ...state,

      dprDetails:
        state.dprDetails?.id === dprDetailId
          ? {
              ...state.dprDetails,
              dprActivitiesDurationHistory,
            }
          : state.dprDetails,
      dprDetailsList: state.dprDetailsList.map((x: DprDetails) =>
        x.id === dprDetailId
          ? {
              ...x,
              dprActivitiesDurationHistory,
            }
          : x
      ),
    })
  ),
  on(
    OfflineActions.setDprActivities,
    (
      state,
      {
        dprActivities,
        dprParallelActivities,
        firstWarning,
        warningStatusTab,
        dprDetailId,
      }
    ) => ({
      ...state,
      dprDetails:
        state.dprDetails?.id === dprDetailId
          ? {
              ...state.dprDetails,
              dprActivities,
              dprParallelActivities,
              firstWarning,
              warningStatusTab,
            }
          : state.dprDetails,
      dprDetailsList: state.dprDetailsList.map((x: DprDetails) =>
        x.id === dprDetailId
          ? {
              ...x,
              dprActivities,
              dprParallelActivities,
              firstWarning,
              warningStatusTab,
            }
          : x
      ),
    })
  ),
  on(
    OfflineActions.setDprStatusTracker,
    (state, { dprStatusTracker, dprDetailId }) => ({
      ...state,
      ...setDprItem(state, 'dprStatusTracker', dprStatusTracker, dprDetailId),
    })
  ),
  on(OfflineActions.setDprWeather, (state, { dprWeather, dprDetailId }) => ({
    ...state,
    ...setDprItem(state, 'dprWeather', dprWeather, dprDetailId),
  })),
  on(OfflineActions.setDprHse, (state, { dprHse, dprDetailId }) => ({
    ...state,
    ...setDprItem(state, 'dprHSE', dprHse, dprDetailId),
  })),
  on(
    OfflineActions.setDprPersonnel,
    (state, { dprPersonnel, dprDetailId }) => ({
      ...state,
      ...setDprItem(state, 'dprPersonnel', dprPersonnel, dprDetailId),
    })
  ),
  on(
    OfflineActions.setDprConsumable,
    (state, { dprConsumable, dprDetailId }) => ({
      ...state,
      ...setDprItem(state, 'dprConsumable', dprConsumable, dprDetailId),
    })
  ),
  on(
    OfflineActions.setDprSummary,
    (state, { dprSummary, firstWarning, warningStatusTab, dprDetailId }) => ({
      ...state,
      ...setDprItem(
        state,
        'dprSummary',
        dprSummary,
        dprDetailId,
        firstWarning,
        warningStatusTab
      ),
    })
  ),
  on(
    OfflineActions.setDprForecastActivities,
    (
      state,
      { dprForecastActivities, firstWarning, warningStatusTab, dprDetailId }
    ) => ({
      ...state,
      dprDetails:
        state.dprDetails?.id === dprDetailId
          ? {
              ...state.dprDetails,
              dprForecastActivities,
              firstWarning,
              warningStatusTab,
            }
          : state.dprDetails,
      dprDetailsList: state.dprDetailsList.map((x: DprDetails) =>
        x.id === dprDetailId
          ? {
              ...x,
              dprForecastActivities,
              firstWarning,
              warningStatusTab,
            }
          : x
      ),
    })
  ),
  on(
    OfflineActions.setDprSummaryExternalComment,
    (state, { dprSummary, firstWarning, warningStatusTab, dprDetailId }) => ({
      ...state,
      ...setDprItem(
        state,
        'dprSummary',
        dprSummary,
        dprDetailId,
        firstWarning,
        warningStatusTab
      ),
    })
  ),
  on(
    OfflineActions.setDprAppendix,
    (
      state,
      { dprAppendixAttachments, firstWarning, warningStatusTab, dprDetailId }
    ) => ({
      ...state,
      ...setDprItem(
        state,
        'dprAppendixAttachments',
        dprAppendixAttachments,
        dprDetailId,
        firstWarning,
        warningStatusTab
      ),
    })
  ),
  on(
    OfflineActions.setDprFreeTextField,
    (state, { dprFreeTextField, dprDetailId }) => ({
      ...state,
      ...setDprListItem(
        state,
        'dprFreeTextFields',
        dprFreeTextField,
        dprDetailId,
        'freeTextFieldId',
        dprFreeTextField.freeTextFieldId
      ),
    })
  ),
  on(OfflineActions.setDprProgress, (state, { dprProgress, dprDetailId }) => ({
    ...state,
    ...setDprItem(state, 'dprProgress', dprProgress, dprDetailId),
  })),
  on(
    OfflineActions.setDprProgressWithoutTarget,
    (state, { dprProgressWithoutTarget, dprDetailId }) => ({
      ...state,
      ...setDprItem(
        state,
        'dprProgressWithoutTarget',
        dprProgressWithoutTarget,
        dprDetailId
      ),
    })
  ),
  on(
    OfflineActions.setDprTableOfColumns,
    (state, { dprTableOfColumn, dprDetailId }) => ({
      ...state,
      ...setDprListItem(
        state,
        'dprTableOfColumns',
        dprTableOfColumn,
        dprDetailId,
        'tableOfColumnId',
        dprTableOfColumn.tableOfColumnId
      ),
    })
  ),
  on(
    OfflineActions.setDprHidden,
    (state, { dprHidden, firstWarning, warningStatusTab, dprDetailId }) => ({
      ...state,
      ...setDprItem(
        state,
        'dprHidden',
        dprHidden,
        dprDetailId,
        firstWarning,
        warningStatusTab
      ),
    })
  ),

  //#endregion Dpr Detail

  on(OfflineActions.setSyncOfflineGuid, (state) => ({
    ...state,
    syncOfflineGuid: String.isNullOrWhiteSpace(state.syncOfflineGuid)
      ? uuid()
      : state.syncOfflineGuid,
  })),
  on(OfflineActions.deleteSyncOfflineGuid, (state) => ({
    ...state,
    syncOfflineGuid: null,
  }))
);

function getUpdatedDprOnlineApprovals(
  dprOnlineApprovals: DprOnlineApproval[],
  dprOnlineApproval: DprOnlineApproval
): DprOnlineApproval[] {
  if (dprOnlineApprovals?.find((x) => x.id === dprOnlineApproval?.id)) {
    return dprOnlineApprovals.map((x) =>
      x.id === dprOnlineApproval?.id
        ? { ...x, comment: dprOnlineApproval.comment }
        : x
    );
  } else {
    return dprOnlineApproval
      ? [...dprOnlineApprovals, dprOnlineApproval]
      : dprOnlineApprovals;
  }
}

function isExtraDprDetails(
  state: OfflineState,
  dprDetailId: number | string
): boolean {
  return (
    state.dprDetails?.id === dprDetailId ||
    !(state.dprDetailsList ?? []).some((x) => x.id === dprDetailId)
  );
}

function setDprItem(
  state: OfflineState,
  property: string,
  data: any,
  dprDetailId: number | string,
  firstWarning: WarningType = null,
  warningStatusTab: string = null
): object {
  return {
    dprDetails:
      state.dprDetails?.id === dprDetailId
        ? {
            ...state.dprDetails,
            [property]: data,
            firstWarning: firstWarning ?? data.firstWarning,
            warningStatusTab: warningStatusTab ?? data.warningStatusTab,
          }
        : state.dprDetails,
    dprDetailsList: state.dprDetailsList?.map((x: DprDetails) =>
      x.id === dprDetailId
        ? {
            ...x,
            [property]: data,
            firstWarning: firstWarning ?? data.firstWarning,
            warningStatusTab: warningStatusTab ?? data.warningStatusTab,
          }
        : x
    ),
  };
}

function setDprListItem(
  state: OfflineState,
  property: string,
  data: any,
  dprDetailId: number | string,
  idProperty: string,
  id: string | number,
  firstWarning: WarningType = null,
  warningStatusTab: string = null
): object {
  return {
    dprDetails:
      state.dprDetails?.id === dprDetailId
        ? {
            ...state.dprDetails,
            [property]: state.dprDetails[property].find(
              (x) => x[idProperty] === id
            )
              ? (state.dprDetails[property] as []).map((x) =>
                  x[idProperty] === id ? data : x
                )
              : [...state.dprDetails[property], data],
            firstWarning: firstWarning ?? data.firstWarning,
            warningStatusTab: warningStatusTab ?? data.warningStatusTab,
          }
        : state.dprDetails,
    dprDetailsList: state.dprDetailsList?.map((x: DprDetails) =>
      x.id === dprDetailId
        ? {
            ...x,
            [property]: (x[property] as []).find((y) => y[idProperty] === id)
              ? (x[property] as []).map((y) =>
                  y[idProperty] === id ? data : y
                )
              : [...(x[property] as []), data],
            firstWarning: firstWarning ?? data.firstWarning,
            warningStatusTab: warningStatusTab ?? data.warningStatusTab,
          }
        : x
    ),
  };
}
